/**
 * Anonymous + session ID manager with cookie + localStorage fallback.
 */
import type { CookieAdapter, StorageAdapter } from "./storage";
import { ulid } from "./ulid";
import { now } from "./utils";

export type Ids = {
  anonId: string;
  sessionId: string;
};

export type IdOptions = {
  anonKey?: string;
  sessionKey?: string;
  sessionLastKey?: string;
  sessionTtlMs?: number;
};

const defaultOptions: Required<IdOptions> = {
  anonKey: "prospix_anon_id",
  sessionKey: "prospix_session_id",
  sessionLastKey: "prospix_session_last",
  sessionTtlMs: 30 * 60 * 1000,
};

export function createIdManager(
  local: StorageAdapter,
  cookies: CookieAdapter,
  options: IdOptions = {},
) {
  const cfg = { ...defaultOptions, ...options };

  function readId(key: string): string | null {
    return cookies.get(key) ?? local.get(key);
  }

  function writeId(key: string, value: string, maxAgeSeconds: number) {
    cookies.set(key, value, { maxAgeSeconds });
    local.set(key, value);
  }

  function getAnonId(): string {
    const existing = readId(cfg.anonKey);
    if (existing) return existing;
    const id = ulid();
    writeId(cfg.anonKey, id, 60 * 60 * 24 * 365);
    return id;
  }

  function getSessionId(): string {
    const last = Number(readId(cfg.sessionLastKey) ?? "0");
    const existing = readId(cfg.sessionKey);
    const expired = now() - last > cfg.sessionTtlMs;
    if (!existing || expired) {
      const id = ulid();
      writeId(cfg.sessionKey, id, Math.floor(cfg.sessionTtlMs / 1000));
      writeId(
        cfg.sessionLastKey,
        String(now()),
        Math.floor(cfg.sessionTtlMs / 1000),
      );
      return id;
    }
    writeId(
      cfg.sessionLastKey,
      String(now()),
      Math.floor(cfg.sessionTtlMs / 1000),
    );
    return existing;
  }

  function resetSession(): string {
    const id = ulid();
    writeId(cfg.sessionKey, id, Math.floor(cfg.sessionTtlMs / 1000));
    writeId(
      cfg.sessionLastKey,
      String(now()),
      Math.floor(cfg.sessionTtlMs / 1000),
    );
    return id;
  }

  function getIds(): Ids {
    return { anonId: getAnonId(), sessionId: getSessionId() };
  }

  return { getIds, resetSession, getAnonId, getSessionId };
}
